import type { DetectionBox, Detector } from './tracking/Detector';
import type { PoseEngine, RawPose, RawPoseResult } from './PoseEngine';
import { pickPrimaryRawPose } from './PrimaryBodyPick';

export interface CroppedPoseOptions {
  padding?: number;
  maxPeople?: number;
  minBoxArea?: number;
}

function selectTopPersons(
  detections: readonly DetectionBox[],
  minBoxArea: number,
  maxPeople: number,
): DetectionBox[] {
  if (maxPeople <= 0) return [];

  const selected: DetectionBox[] = [];
  for (const detection of detections) {
    if (detection.class !== 'person' || detection.w * detection.h < minBoxArea) continue;

    let insertAt = 0;
    while (insertAt < selected.length && selected[insertAt].score >= detection.score) {
      insertAt += 1;
    }
    if (insertAt >= maxPeople) continue;

    selected.splice(insertAt, 0, detection);
    if (selected.length > maxPeople) selected.pop();
  }
  return selected;
}

export class CroppedPoseOrchestrator {
  private readonly cropCanvas: HTMLCanvasElement;
  private readonly cropCtx: CanvasRenderingContext2D | null;
  private readonly opts: Required<CroppedPoseOptions>;
  private detectionInferMs = 0;
  private seq = 0;

  constructor(
    private readonly detector: Detector,
    private readonly engine: PoseEngine,
    opts: CroppedPoseOptions = {},
  ) {
    this.cropCanvas = document.createElement('canvas');
    this.cropCtx = this.cropCanvas.getContext('2d');
    this.opts = {
      padding: opts.padding ?? 0.15,
      maxPeople: opts.maxPeople ?? 5,
      minBoxArea: opts.minBoxArea ?? 0.0005,
    };
  }

  lastDetectionInferMs(): number {
    return this.detectionInferMs;
  }

  async run(video: HTMLVideoElement, baseTimestamp: number): Promise<RawPoseResult> {
    const W = video.videoWidth;
    const H = video.videoHeight;
    if (W <= 0 || H <= 0) return { poses: [] };

    const detResult = await this.detector.detect(video);
    this.detectionInferMs = detResult.inferenceTimeMs;
    const persons = selectTopPersons(
      detResult.detections,
      this.opts.minBoxArea,
      this.opts.maxPeople,
    );

    const ctx = this.cropCtx;
    if (!ctx) return { poses: [] };

    const out: RawPose[] = [];
    const pad = this.opts.padding;
    for (const p of persons) {
      const sx = Math.max(0, (p.x - p.w * pad) * W);
      const sy = Math.max(0, (p.y - p.h * pad) * H);
      const ex = Math.min(W, (p.x + p.w * (1 + pad)) * W);
      const ey = Math.min(H, (p.y + p.h * (1 + pad)) * H);
      const sw = ex - sx;
      const sh = ey - sy;
      if (sw < 16 || sh < 16) continue;

      this.cropCanvas.width = Math.round(sw);
      this.cropCanvas.height = Math.round(sh);
      ctx.drawImage(video, sx, sy, sw, sh, 0, 0, this.cropCanvas.width, this.cropCanvas.height);

      this.seq = (this.seq + 1) & 0x0fffff;
      const raw = await Promise.resolve(
        this.engine.detectForVideo(this.cropCanvas, baseTimestamp + this.seq),
      );
      // The pose engine may emit several candidates per crop (mirrors
      // / partial reflections inside the bbox). pickPrimaryRawPose
      // ranks by torso-landmark mean visibility and returns the most
      // plausible single subject. Empty results are skipped.
      const { pose: first } = pickPrimaryRawPose(raw);
      if (!first) continue;

      out.push({
        landmarks: first.landmarks.map((lm) => ({
          // Remap 2D pixel coords from crop-local back to full-frame.
          x: (sx + lm.x * sw) / W,
          y: (sy + lm.y * sh) / H,
          score: lm.score,
          // Preserve world coords as-is — MediaPipe's pose_world_landmarks
          // are hip-centred metres and do not depend on the input bbox,
          // so they need no remap. Without this passthrough the canonical
          // pipeline silently lost 3D / ROM signal for crop-pipeline
          // recordings.
          worldX: lm.worldX,
          worldY: lm.worldY,
          worldZ: lm.worldZ,
          worldVisibility: lm.worldVisibility,
        })),
      });
    }
    return { poses: out };
  }
}
